import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-row justify-between items-center w-full" }
const _hoisted_2 = { class: "flex flex-row gap-1 items-center" }
const _hoisted_3 = { class: "title" }
const _hoisted_4 = { class: "title" }
const _hoisted_5 = { class: "title" }

import { computed, ref, watchEffect } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";
import {
  addressList,
  addressCreate,
  addressUpdate,
  addressDelete,
} from "@/common/api/rest/address";
import {
  contactList,
  contactCreate,
  contactUpdate,
  contactDelete,
} from "@/common/api/rest/contact";
import { laboratoryGet, laboratoryUpdate } from "@/common/api/rest/laboratory";

import type { AddressType, ContactType } from "@/common/types/common";
import type {
  LaboratoryType,
  LaboratoryPayloadType,
} from "@/common/types/laboratory";

import { RegisterTheme } from "@/common/themes";
import { addressForm, contactForm, laboratoryForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: '[laboratory]',
  setup(__props) {

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const addressData = ref<AddressType[]>([]);
const contactData = ref<AddressType[]>([]);

const selectedMenu = ref<string>("home");
const laboratoryData = ref<LaboratoryType | null>(null);
const laboratoryPayload = ref<LaboratoryPayloadType | null>(null);
const addressPayload = ref<AddressType | null>(null);
const contactPayload = ref<ContactType | null>(null);

const selectedAddress = ref<AddressType | null>(null);
const selectedContact = ref<ContactType | null>(null);

const addressEdit = ref(false);
const showModalAddress = ref(false);
const showDelAddress = ref(false);

const contactEdit = ref(false);
const showModalContact = ref(false);
const showDelContact = ref(false);

const showDelAvatar = ref(false);

const accountId = computed(() => route.params.id as string);
const laboratoryId = computed(() => route.params.laboratory as string);

const addressHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "address", label: t("common.labels.address") },
  { key: "cep", label: t("common.labels.cep") },
]);

const contactHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "contact", label: t("common.labels.contact") },
]);

const modalAddressTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.address"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.address"),
      }).toUpperCase(),
);

const modalContactTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.contact"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.contact"),
      }).toUpperCase(),
);

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.home"),
  },
  {
    key: "address",
    icon: "pin",
    label: t("common.labels.address"),
  },
  {
    key: "contact",
    icon: "phone",
    label: t("common.labels.contacts"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/laboratory`);
}

function fetchLaboratory() {
  laboratoryGet(accountId.value, laboratoryId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        laboratoryData.value = data;

        laboratoryPayload.value = {
          name: data.name,
          fantasy_name: data.fantasy_name,
          description: data.description,
          document_name: data.document_name,
          document_number: data.document_number,
          document_type: data.document_type,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateLaboratory(data: LaboratoryPayloadType) {
  laboratoryUpdate(accountId.value, laboratoryId.value, data)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// ADDRESS

function fetchAddress() {
  addressList(accountId.value, laboratoryId.value, {
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        addressData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAddress() {
  addressEdit.value = false;
  showModalAddress.value = true;
}

function addressCancel() {
  addressEdit.value = false;
  showModalAddress.value = false;
  addressPayload.value = null;
}

function addAddressConfirm() {
  if (!addressPayload.value) return;

  store.commit("SET_LOADING", true);

  addressCreate(accountId.value, laboratoryId.value, addressPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAddress(data: AddressType) {
  selectedAddress.value = data;
  addressPayload.value = data;
  showModalAddress.value = true;
  addressEdit.value = true;
}

function updateAddressConfirm() {
  if (!selectedAddress.value) return;

  store.commit("SET_LOADING", true);

  addressUpdate(
    accountId.value,
    selectedAddress.value.address_id as string,
    addressPayload.value as AddressType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitAddress() {
  if (!addressEdit.value) addAddressConfirm();
  else updateAddressConfirm();
}

function removeAddress(data: AddressType) {
  selectedAddress.value = data;
  showDelAddress.value = true;
}

function removeAddressCancel() {
  selectedAddress.value = null;
  showDelAddress.value = false;
}

function removeAddressConfirm() {
  if (!selectedAddress.value) return;

  store.commit("SET_LOADING", true);

  addressDelete(accountId.value, selectedAddress.value.address_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        removeAddressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// CONTACT

function fetchContact() {
  contactList(accountId.value, laboratoryId.value, {
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        contactData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addContact() {
  showModalContact.value = true;
}

function contactCancel() {
  contactEdit.value = false;
  showModalContact.value = false;
  contactPayload.value = null;
}

function addContactConfirm() {
  if (!contactPayload.value) return;

  store.commit("SET_LOADING", true);

  contactCreate(accountId.value, laboratoryId.value, contactPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updatedContact(data: ContactType) {
  contactEdit.value = true;
  contactPayload.value = data;
  selectedContact.value = data;
  showModalContact.value = true;
}

function updatedContactConfirm() {
  if (!contactPayload.value && !selectedContact.value) return;

  store.commit("SET_LOADING", true);

  contactUpdate(
    accountId.value,
    selectedContact.value?.contact_id as string,
    contactPayload.value as ContactType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitContact() {
  if (!contactEdit.value) addContactConfirm();
  else updatedContactConfirm();
}

function removeContact(data: ContactType) {
  selectedContact.value = data;
  showDelContact.value = true;
}

function removeContactCancel() {
  selectedContact.value = null;
  showDelContact.value = false;
}

function removeContactConfirm() {
  if (!selectedContact.value) return;

  store.commit("SET_LOADING", true);

  contactDelete(accountId.value, selectedContact.value?.contact_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        removeContactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// AVATAR

function addAvatar(avatar: string) {
  fileAvatarCreate(accountId.value, {
    owner_id: laboratoryId.value,
    type: "LABORATORY",
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(avatar: string) {
  if (!laboratoryData.value?.avatar) return;

  fileAvatarUpdate(accountId.value, laboratoryData.value.avatar.avatar_id, {
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (laboratoryData.value?.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarCancel() {
  showDelAvatar.value = false;
}

function delAvatarConfirm() {
  if (!laboratoryData.value?.avatar) return;

  fileAvatarDelete(accountId.value, laboratoryData.value!.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        showDelAvatar.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceForm = debounce(updateLaboratory, 1000);

watchEffect(() => {
  if (laboratoryId.value) {
    if (selectedMenu.value === "home") fetchLaboratory();
    else if (selectedMenu.value === "address") fetchAddress();
    else if (selectedMenu.value === "contact") fetchContact();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "laboratory" }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_unref(svgIcon), {
              icon: "laboratory",
              size: "2rem"
            }),
            _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.edit", { concat: _unref(t)("common.labels.laboratory") })), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-back btn-sm",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (back()))
          }, [
            _createVNode(_unref(svgIcon), {
              icon: "close",
              size: "1.25rem"
            })
          ])
        ])
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(iconMenu), {
          tabs: menuTabs.value,
          active: selectedMenu.value,
          onSelected: _cache[4] || (_cache[4] = ($event: any) => (selectedMenu.value = $event))
        }, {
          content: _withCtx(() => [
            (selectedMenu.value === 'home')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("common.labels.laboratory")), 1),
                  _createVNode(_unref(laboratoryForm), {
                    width: "100%",
                    data: laboratoryPayload.value,
                    "show-avatar": true,
                    avatar: laboratoryData.value?.avatar?.small,
                    onUpdateData: _unref(debounceForm),
                    onAvatarAdd: changeAvatar,
                    onAvatarDel: _cache[1] || (_cache[1] = ($event: any) => (delAvatar()))
                  }, null, 8, ["data", "avatar", "onUpdateData"])
                ], 64))
              : _createCommentVNode("", true),
            (selectedMenu.value === 'address')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("common.labels.address")), 1),
                  _createVNode(_unref(baseTable), {
                    header: addressHeader.value,
                    data: addressData.value,
                    paginated: false,
                    "show-search": false,
                    options: true,
                    edit: true,
                    del: true,
                    "onBtn:add": _cache[2] || (_cache[2] = ($event: any) => (addAddress())),
                    "onBtn:edit": updateAddress,
                    "onBtn:del": removeAddress
                  }, {
                    address: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(data.city) + " - " + _toDisplayString(data.state), 1)
                    ]),
                    _: 1
                  }, 8, ["header", "data"])
                ], 64))
              : _createCommentVNode("", true),
            (selectedMenu.value === 'contact')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)("common.labels.contact")), 1),
                  _createVNode(_unref(baseTable), {
                    header: contactHeader.value,
                    data: contactData.value,
                    paginated: false,
                    "show-search": false,
                    options: true,
                    edit: true,
                    del: true,
                    "onBtn:add": _cache[3] || (_cache[3] = ($event: any) => (addContact())),
                    "onBtn:edit": updatedContact,
                    "onBtn:del": removeContact
                  }, {
                    type: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(_unref(t)(`common.labels.${data.type}`)), 1)
                    ]),
                    _: 1
                  }, 8, ["header", "data"])
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["tabs", "active"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseModal), {
      show: showModalAddress.value,
      title: modalAddressTitle.value,
      "show-button-confirm": true,
      "button-confirm": 
      addressEdit.value ? _unref(t)('common.labels.save') : _unref(t)('common.labels.add')
    ,
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[6] || (_cache[6] = ($event: any) => (addressCancel())),
      onConfirm: _cache[7] || (_cache[7] = ($event: any) => (submitAddress()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(addressForm), {
          data: addressPayload.value,
          onUpdateData: _cache[5] || (_cache[5] = ($event: any) => (addressPayload.value = $event))
        }, null, 8, ["data"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showDelAddress.value,
      title: _unref(t)('common.labels.remove', { concat: _unref(t)('common.labels.company') }),
      message: 
      _unref(t)('common.texts.remove_message', { concat: selectedAddress.value?.name })
    ,
      onCancel: removeAddressCancel,
      onConfirm: _cache[8] || (_cache[8] = ($event: any) => (removeAddressConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseModal), {
      show: showModalContact.value,
      title: modalContactTitle.value,
      "show-button-confirm": true,
      "button-confirm": 
      contactEdit.value ? _unref(t)('common.labels.save') : _unref(t)('common.labels.add')
    ,
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[10] || (_cache[10] = ($event: any) => (contactCancel())),
      onConfirm: _cache[11] || (_cache[11] = ($event: any) => (submitContact()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(contactForm), {
          data: contactPayload.value,
          onUpdateData: _cache[9] || (_cache[9] = ($event: any) => (contactPayload.value = $event))
        }, null, 8, ["data"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showDelContact.value,
      title: _unref(t)('common.labels.remove', { concat: _unref(t)('common.labels.contact') }),
      message: 
      _unref(t)('common.texts.remove_message', { concat: selectedContact.value?.name })
    ,
      onCancel: removeContactCancel,
      onConfirm: _cache[12] || (_cache[12] = ($event: any) => (removeContactConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseConfirm), {
      show: showDelAvatar.value,
      title: _unref(t)('common.labels.remove', { concat: _unref(t)('common.labels.avatar') }),
      message: 
      _unref(t)('common.texts.remove_message', { concat: _unref(t)('common.labels.avatar') })
    ,
      onCancel: delAvatarCancel,
      onConfirm: _cache[13] || (_cache[13] = ($event: any) => (delAvatarConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})